import { useEffect, useState } from "react";
import type { LayoutChangeEvent } from "react-native";

import { WithRealSafeAreaInsets } from "@/hooks/useRealSafeAreaInsets";
import { Loader } from "@whoosh/design-system/src/components/display";
import { StandardModal } from "@whoosh/design-system/src/components/overlay/StandardModal";
import { Box, VStack } from "@whoosh/design-system/src/components/primitive";

import { AvailableRewardsView } from "./components/AvailableRewardsView";
import { BalanceCard } from "./components/BalanceCard";
import {
  ExpiringPointsSchedule,
  type LoyaltyPointExpiration,
} from "./components/ExpiringPointsSchedule";
import { RewardsMenu } from "./components/RewardsMenu";
import {
  RewardsModalBackHeader,
  RewardsModalHeader,
} from "./components/RewardsModalHeader";

export type LoyaltyReward = {
  readonly id: string;
  readonly name: string;
  readonly points: number;
  readonly status: "AVAILABLE" | "REQUIRES_ORDER_ITEM" | "UNAVAILABLE";
};

type LoyaltyRewardsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  currentBalance: number;
  rewards: readonly LoyaltyReward[];
  pointTerminologyOne: string;
  pointTerminologyOther: string;
  pointExpirations?: readonly LoyaltyPointExpiration[];
  timeZone?: string;
  readOnly?: boolean;
  isLoading?: boolean;
  onSelectReward?: (reward: LoyaltyReward) => void;
};

type RewardsModalView = "available-rewards" | "all-rewards" | "expiring-points";
export const LoyaltyRewardsModal = ({
  isOpen,
  onClose,
  currentBalance,
  rewards,
  pointTerminologyOne,
  pointTerminologyOther,
  pointExpirations = [],
  timeZone = "Etc/UTC",
  readOnly = false,
  isLoading = false,
  onSelectReward,
}: LoyaltyRewardsModalProps) => {
  const [modalView, setModalView] =
    useState<RewardsModalView>("available-rewards");

  // The parent can close this modal directly (e.g. after a successful reward
  // application) without calling onClose, so reset the view whenever the
  // modal becomes closed rather than relying on that callback.
  useEffect(() => {
    if (!isOpen) {
      setModalView("available-rewards");
    }
  }, [isOpen]);
  const [rewardPointsPillWidth, setRewardPointsPillWidth] = useState<
    number | undefined
  >();
  const sortedRewards = [...rewards].sort(
    (rewardA, rewardB) => rewardA.points - rewardB.points
  );

  const handleRewardPointsPillLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    const measuredWidth = Math.ceil(nativeEvent.layout.width);

    setRewardPointsPillWidth((currentWidth) =>
      currentWidth && currentWidth >= measuredWidth
        ? currentWidth
        : measuredWidth
    );
  };
  const expiringPointsTotal = pointExpirations.reduce(
    (total, expiration) => total + expiration.points,
    0
  );
  const modalContent =
    modalView === "all-rewards" ? (
      <RewardsMenu
        rewards={sortedRewards}
        pointTerminologyOne={pointTerminologyOne}
        pointTerminologyOther={pointTerminologyOther}
        readOnly={readOnly || isLoading}
        onSelectReward={onSelectReward}
        pointsPillWidth={rewardPointsPillWidth}
        onPointsPillLayout={handleRewardPointsPillLayout}
      />
    ) : modalView === "expiring-points" ? (
      <ExpiringPointsSchedule
        expirations={pointExpirations}
        pointTerminologyOther={pointTerminologyOther}
        timeZone={timeZone}
      />
    ) : (
      <VStack gap={6}>
        <BalanceCard
          currentBalance={currentBalance}
          pointTerminologyOne={pointTerminologyOne}
          pointTerminologyOther={pointTerminologyOther}
        />
        <AvailableRewardsView
          rewards={sortedRewards}
          pointTerminologyOne={pointTerminologyOne}
          pointTerminologyOther={pointTerminologyOther}
          expiringPointsTotal={expiringPointsTotal}
          readOnly={readOnly || isLoading}
          onSelectReward={onSelectReward}
          onViewAllRewards={() => setModalView("all-rewards")}
          onViewExpiringPoints={() => setModalView("expiring-points")}
        />
      </VStack>
    );

  return (
    <WithRealSafeAreaInsets>
      <StandardModal
        isOpen={isOpen}
        onClose={onClose}
        upperNode={
          modalView === "available-rewards" ? (
            <RewardsModalHeader onClose={onClose} />
          ) : (
            <RewardsModalBackHeader
              onBack={() => setModalView("available-rewards")}
            />
          )
        }
        hideCloseButton
        hideFooterPadding
        content={
          <Box px={6} pb={6} position="relative">
            {modalContent}
            {isLoading ? <Loader /> : null}
          </Box>
        }
      />
    </WithRealSafeAreaInsets>
  );
};
